/**
 * Parent Requests Admin Page - staff-facing triage for the six parent/
 * student self-service systems: absence requests, document requests,
 * consent forms (permission slips), complaints, acknowledgements, and
 * fee reminders. Each tab talks to an already-shipped backend module
 * (see routers/absence_requests.py etc.) via parentServicesService.js —
 * this page adds no new backend endpoints, it's the missing admin UI for
 * workflows that were previously only exercised via curl.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import parentServicesService from '../services/parentServicesService';
import studentsService from '../services/studentsService';
import classesService from '../services/classesService';
import { CircleNotch } from '@phosphor-icons/react';
import './ParentRequestsAdminPage.css';

const TABS = [
  { key: 'absence', label: 'Absence Requests', roles: ['super_admin', 'school_admin', 'teacher', 'registrar'] },
  { key: 'documents', label: 'Document Requests', roles: ['super_admin', 'school_admin', 'registrar'] },
  { key: 'consent', label: 'Permission Slips', roles: ['super_admin', 'school_admin', 'teacher', 'registrar'] },
  { key: 'complaints', label: 'Complaints', roles: ['super_admin', 'school_admin', 'hr', 'registrar'] },
  { key: 'acknowledgements', label: 'Acknowledgements', roles: ['super_admin', 'school_admin', 'teacher', 'registrar'] },
  { key: 'fee-reminders', label: 'Fee Reminders', roles: ['super_admin', 'school_admin'] },
];

const STATUS_COLORS = {
  pending: '#6b7280', submitted: '#6b7280',
  approved: '#10b981', granted: '#10b981', resolved: '#10b981', completed: '#10b981', sent: '#10b981', ready: '#10b981',
  rejected: '#ef4444', declined: '#ef4444', dismissed: '#ef4444', failed: '#ef4444',
  cancelled: '#9ca3af',
  in_progress: '#3b82f6', under_review: '#3b82f6',
};

function statusColor(status) {
  return STATUS_COLORS[status] || '#6b7280';
}

function StatusBadge({ status }) {
  if (!status) return <span>—</span>;
  return (
    <span className="badge" style={{ backgroundColor: statusColor(status) }}>
      {status.replace(/_/g, ' ').toUpperCase()}
    </span>
  );
}

function ParentRequestsAdminPage() {
  const { user } = useAuth();
  const visibleTabs = TABS.filter((t) => t.roles.includes(user?.role));
  const [tab, setTab] = useState(visibleTabs[0]?.key || 'absence');

  return (
    <Layout>
      <div className="page parent-requests-admin-page">
        <div className="page-header">
          <div>
            <h1>Parent &amp; Student Requests</h1>
            <p>Review absence notices, document requests, permission slips, complaints, acknowledgements and fee reminders</p>
          </div>
        </div>

        <div className="pra-tabs">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={tab === t.key ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {visibleTabs.length === 0 && (
          <div className="empty-state"><p>You don't have access to any of these request types.</p></div>
        )}

        {tab === 'absence' && <AbsenceRequestsTab />}
        {tab === 'documents' && <DocumentRequestsTab />}
        {tab === 'consent' && <ConsentFormsTab />}
        {tab === 'complaints' && <ComplaintsTab currentRole={user?.role} />}
        {tab === 'acknowledgements' && <AcknowledgementsTab />}
        {tab === 'fee-reminders' && <FeeRemindersTab />}
      </div>
    </Layout>
  );
}

/* ---------------------------------------------------------------- */
/* Absence Requests                                                   */
/* ---------------------------------------------------------------- */

function AbsenceRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await parentServicesService.listAbsenceRequests(statusFilter ? { status_filter: statusFilter } : {});
      setItems(data || []);
    } catch (error) {
      toast.error('Failed to load absence requests: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openReview = (item, decision) => {
    setReviewTarget({ item, decision });
    setReviewNotes('');
  };

  const submitReview = async () => {
    if (!reviewTarget) return;
    setSubmitting(true);
    try {
      await parentServicesService.reviewAbsenceRequest(reviewTarget.item.id, {
        status: reviewTarget.decision,
        review_notes: reviewNotes || null,
      });
      toast.success(`Request ${reviewTarget.decision}`);
      setReviewTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to review request: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pra-panel">
      <div className="filters">
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="empty-state"><p>No absence requests found</p></div>
      ) : (
        <div className="pra-table">
          <table>
            <thead>
              <tr><th>Student</th><th>Type</th><th>Dates</th><th>Reason</th><th>Status</th><th>Reviewed By</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.student_name || item.student_id}</td>
                  <td>{item.request_type === 'late_arrival' ? 'Running Late' : 'Absence'}</td>
                  <td>{item.request_type === 'late_arrival' ? `${item.start_date}, ~${item.expected_arrival_time}` : `${item.start_date} to ${item.end_date}`}</td>
                  <td>{item.reason}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>{item.reviewed_by || '—'}</td>
                  <td>
                    {item.status === 'pending' ? (
                      <div className="pra-row-actions">
                        <button className="btn btn-sm btn-success" onClick={() => openReview(item, 'approved')}>Approve</button>
                        <button className="btn btn-sm btn-danger" onClick={() => openReview(item, 'rejected')}>Reject</button>
                      </div>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reviewTarget && (
        <div className="modal-overlay" onClick={() => setReviewTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{reviewTarget.decision === 'approved' ? 'Approve' : 'Reject'} Absence Request</h2>
              <button className="btn-close" onClick={() => setReviewTarget(null)}>×</button>
            </div>
            <div className="form-group">
              <label>Notes (optional, visible to the parent)</label>
              <textarea rows="3" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Notes" />
            </div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setReviewTarget(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={submitting} onClick={submitReview}>
                {submitting ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Document Requests                                                  */
/* ---------------------------------------------------------------- */

const DOCUMENT_TYPE_LABELS = {
  transfer_certificate: 'Transfer Certificate',
  leaving_certificate: 'Leaving Certificate',
  transcript: 'Transcript',
  bonafide_letter: 'Bonafide Letter',
  id_card_replacement: 'ID Card Replacement',
  other: 'Other',
};

function DocumentRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [manageTarget, setManageTarget] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('in_progress');
  const [reviewNotes, setReviewNotes] = useState('');
  const [certificateIssuanceId, setCertificateIssuanceId] = useState('');
  const [fulfillmentFileUrl, setFulfillmentFileUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await parentServicesService.listDocumentRequests(statusFilter ? { status_filter: statusFilter } : {});
      setItems(data || []);
    } catch (error) {
      toast.error('Failed to load document requests: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openManage = (item) => {
    setManageTarget(item);
    setReviewStatus('in_progress');
    setReviewNotes('');
    setCertificateIssuanceId('');
    setFulfillmentFileUrl('');
  };

  const saveReview = async () => {
    if (!manageTarget) return;
    setSubmitting(true);
    try {
      await parentServicesService.reviewDocumentRequest(manageTarget.id, { status: reviewStatus, review_notes: reviewNotes || null });
      toast.success('Status updated');
      setManageTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to update status: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fulfill = async () => {
    if (!manageTarget) return;
    if (!certificateIssuanceId.trim() && !fulfillmentFileUrl.trim()) {
      toast.error('Provide a certificate issuance ID or a file URL');
      return;
    }
    setSubmitting(true);
    try {
      await parentServicesService.fulfillDocumentRequest(manageTarget.id, {
        certificate_issuance_id: certificateIssuanceId.trim() || null,
        fulfillment_file_url: fulfillmentFileUrl.trim() || null,
      });
      toast.success('Request marked fulfilled');
      setManageTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to fulfill request: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canFulfill = manageTarget && !['completed', 'cancelled', 'rejected'].includes(manageTarget.status);

  const generateTranscript = async () => {
    if (!manageTarget) return;
    setSubmitting(true);
    try {
      await parentServicesService.generateTranscript(manageTarget.id);
      toast.success('Transcript generated');
      setManageTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to generate transcript: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = async (item) => {
    try {
      if (item.fulfillment_file_url?.includes('/transcript.pdf')) {
        const response = await parentServicesService.downloadTranscript(item.id);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcript_${item.student_id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        window.open(item.fulfillment_file_url, '_blank', 'noreferrer');
      }
    } catch (error) {
      toast.error('Failed to download file: ' + error.message);
    }
  };

  return (
    <div className="pra-panel">
      <div className="filters">
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="ready">Ready</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="empty-state"><p>No document requests found</p></div>
      ) : (
        <div className="pra-table">
          <table>
            <thead>
              <tr><th>Student</th><th>Document</th><th>Reason</th><th>Status</th><th>Fulfillment</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.student_name || item.student_id}</td>
                  <td>{DOCUMENT_TYPE_LABELS[item.document_type] || item.document_type}</td>
                  <td>{item.reason || '—'}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>
                    {item.fulfillment_file_url ? (
                      <button className="btn btn-sm btn-secondary" onClick={() => downloadFile(item)}>Download</button>
                    ) : item.certificate_issuance_id ? 'Linked certificate' : '—'}
                  </td>
                  <td>
                    {!['completed', 'cancelled'].includes(item.status) ? (
                      <button className="btn btn-sm btn-primary" onClick={() => openManage(item)}>Manage</button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {manageTarget && (
        <div className="modal-overlay" onClick={() => setManageTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Document Request</h2>
              <button className="btn-close" onClick={() => setManageTarget(null)}>×</button>
            </div>

            <div className="section">
              <h3>Update status</h3>
              <div className="form-group">
                <label>Status</label>
                <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}>
                  <option value="in_progress">In Progress</option>
                  <option value="ready">Ready</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea rows="2" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} />
              </div>
              <button className="btn btn-secondary" disabled={submitting} onClick={saveReview}>
                {submitting ? 'Saving...' : 'Update Status'}
              </button>
            </div>

            {canFulfill && (
              <div className="section">
                <h3>Fulfil</h3>
                <p className="pra-hint">Link an existing certificate issuance for this student, or attach a file URL directly (for transcripts, letters).</p>
                <div className="form-group">
                  <label>Certificate Issuance ID</label>
                  <input value={certificateIssuanceId} onChange={(e) => setCertificateIssuanceId(e.target.value)} placeholder="e.g. from Certificates > Issuance" />
                </div>
                <div className="form-group">
                  <label>Fulfillment File URL</label>
                  <input value={fulfillmentFileUrl} onChange={(e) => setFulfillmentFileUrl(e.target.value)} placeholder="https://..." />
                </div>
                <button className="btn btn-primary" disabled={submitting} onClick={fulfill}>
                  {submitting ? 'Saving...' : 'Mark Fulfilled'}
                </button>
                {manageTarget.document_type === 'transcript' && (
                  <>
                    <p className="pra-hint" style={{ marginTop: '1rem' }}>Or auto-compile from this student's approved report cards — no upload needed:</p>
                    <button className="btn btn-secondary" disabled={submitting} onClick={generateTranscript}>
                      {submitting ? 'Generating...' : 'Generate Transcript'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Consent Forms / Permission Slips                                   */
/* ---------------------------------------------------------------- */

function ConsentFormsTab() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [classes, setClasses] = useState([]);
  const [createForm, setCreateForm] = useState({ title: '', description: '', consent_type: 'other', target_class_id: '', target_student_ids: '', respond_by: '' });
  const [submitting, setSubmitting] = useState(false);
  const [responsesTarget, setResponsesTarget] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await parentServicesService.listConsentForms();
      setForms(data || []);
    } catch (error) {
      toast.error('Failed to load consent forms: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    setShowCreate(true);
    try {
      const data = await classesService.listClasses();
      setClasses(data || []);
    } catch (error) {
      toast.error('Failed to load classes: ' + error.message);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim() || !createForm.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    if (!createForm.target_class_id && !createForm.target_student_ids.trim()) {
      toast.error('Target a class or list at least one student ID');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: createForm.title,
        description: createForm.description,
        consent_type: createForm.consent_type,
        target_class_id: createForm.target_class_id || null,
        target_student_ids: createForm.target_student_ids.trim()
          ? createForm.target_student_ids.split(',').map((s) => s.trim()).filter(Boolean)
          : null,
        respond_by: createForm.respond_by || null,
      };
      const result = await parentServicesService.createConsentForm(payload);
      toast.success(`Form published to ${result.students_targeted} student(s)`);
      setShowCreate(false);
      setCreateForm({ title: '', description: '', consent_type: 'other', target_class_id: '', target_student_ids: '', respond_by: '' });
      load();
    } catch (error) {
      toast.error('Failed to create form: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openResponses = async (form) => {
    setResponsesTarget(form);
    setResponsesLoading(true);
    try {
      const data = await parentServicesService.listConsentFormResponses(form.id);
      setResponses(data || []);
    } catch (error) {
      toast.error('Failed to load responses: ' + error.message);
    } finally {
      setResponsesLoading(false);
    }
  };

  return (
    <div className="pra-panel">
      <div className="pra-panel-header">
        <button className="btn btn-primary" onClick={openCreate}>Create Consent Form</button>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : forms.length === 0 ? (
        <div className="empty-state"><p>No consent forms published yet</p></div>
      ) : (
        <div className="pra-table">
          <table>
            <thead>
              <tr><th>Title</th><th>Type</th><th>Respond By</th><th>Granted</th><th>Declined</th><th>Pending</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {forms.map((form) => (
                <tr key={form.id}>
                  <td>{form.title}</td>
                  <td>{form.consent_type}</td>
                  <td>{form.respond_by || '—'}</td>
                  <td>{form.response_counts?.granted || 0}</td>
                  <td>{form.response_counts?.declined || 0}</td>
                  <td>{form.response_counts?.pending || 0}</td>
                  <td><button className="btn btn-sm btn-secondary" onClick={() => openResponses(form)}>View Responses</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Consent Form</h2>
              <button className="btn-close" onClick={() => setShowCreate(false)}>×</button>
            </div>
            <form onSubmit={submitCreate}>
              <div className="form-group">
                <label>Title *</label>
                <input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea rows="3" value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Consent Type</label>
                <select value={createForm.consent_type} onChange={(e) => setCreateForm({ ...createForm, consent_type: e.target.value })}>
                  <option value="field_trip">Field Trip</option>
                  <option value="media_release">Media Release</option>
                  <option value="data_sharing">Data Sharing</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Target Class</label>
                <select value={createForm.target_class_id} onChange={(e) => setCreateForm({ ...createForm, target_class_id: e.target.value })}>
                  <option value="">— None —</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Or Target Student IDs (comma-separated)</label>
                <input value={createForm.target_student_ids} onChange={(e) => setCreateForm({ ...createForm, target_student_ids: e.target.value })} placeholder="student-id-1, student-id-2" />
              </div>
              <div className="form-group">
                <label>Respond By (optional)</label>
                <input type="date" value={createForm.respond_by} onChange={(e) => setCreateForm({ ...createForm, respond_by: e.target.value })} />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Publishing...' : 'Publish'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {responsesTarget && (
        <div className="modal-overlay" onClick={() => setResponsesTarget(null)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{responsesTarget.title} — Responses</h2>
              <button className="btn-close" onClick={() => setResponsesTarget(null)}>×</button>
            </div>
            {responsesLoading ? (
              <div className="loading"><CircleNotch size={24} weight="bold" /> Loading...</div>
            ) : responses.length === 0 ? (
              <p className="empty">No responses yet</p>
            ) : (
              <div className="pra-table">
                <table>
                  <thead><tr><th>Student</th><th>Status</th><th>Notes</th><th>Responded</th></tr></thead>
                  <tbody>
                    {responses.map((r) => (
                      <tr key={r.id}>
                        <td>{r.student_name || r.student_id}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td>{r.notes || '—'}</td>
                        <td>{r.responded_at ? new Date(r.responded_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Complaints                                                        */
/* ---------------------------------------------------------------- */

function ComplaintsTab({ currentRole }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await parentServicesService.listComplaints(statusFilter ? { status_filter: statusFilter } : {});
      setItems(data || []);
    } catch (error) {
      toast.error('Failed to load complaints: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (item) => {
    setDetailLoading(true);
    try {
      const data = await parentServicesService.getComplaint(item.id);
      setDetail(data);
      setUpdateStatus(data.status);
      setAssignedTo(data.assigned_to || '');
      setResolutionNotes(data.resolution_notes || '');
      setCommentText('');
      setIsInternal(false);
    } catch (error) {
      toast.error('Failed to load complaint: ' + error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    const data = await parentServicesService.getComplaint(id);
    setDetail(data);
  };

  const addComment = async () => {
    if (!detail || !commentText.trim()) return;
    setSubmitting(true);
    try {
      await parentServicesService.addComplaintComment(detail.id, { comment: commentText, is_internal: isInternal });
      setCommentText('');
      setIsInternal(false);
      await refreshDetail(detail.id);
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const saveUpdate = async () => {
    if (!detail) return;
    setSubmitting(true);
    try {
      await parentServicesService.updateComplaint(detail.id, {
        status: updateStatus || null,
        assigned_to: assignedTo || null,
        resolution_notes: resolutionNotes || null,
      });
      toast.success('Complaint updated');
      await refreshDetail(detail.id);
      load();
    } catch (error) {
      toast.error('Failed to update complaint: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pra-panel">
      <div className="filters">
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="empty-state"><p>No complaints found</p></div>
      ) : (
        <div className="pra-table">
          <table>
            <thead>
              <tr><th>Subject</th><th>Category</th><th>Severity</th><th>Status</th><th>Filed</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.subject}</td>
                  <td>{item.category}</td>
                  <td><span className={`badge severity-${item.severity}`}>{item.severity}</span></td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td><button className="btn btn-sm btn-primary" onClick={() => openDetail(item)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(detail || detailLoading) && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
            ) : (
              <>
                <div className="modal-header">
                  <h2>{detail.subject}</h2>
                  <button className="btn-close" onClick={() => setDetail(null)}>×</button>
                </div>
                <div className="ticket-detail">
                  <div className="ticket-info-grid">
                    <div><strong>Category:</strong><span>{detail.category}</span></div>
                    <div><strong>Severity:</strong><span className={`badge severity-${detail.severity}`}>{detail.severity}</span></div>
                    <div><strong>Status:</strong><StatusBadge status={detail.status} /></div>
                    <div><strong>Filed:</strong><span>{new Date(detail.created_at).toLocaleString()}</span></div>
                  </div>

                  <div className="section">
                    <h3>Description</h3>
                    <p>{detail.description}</p>
                  </div>

                  {detail.resolution_notes && (
                    <div className="section resolution">
                      <h3>Resolution</h3>
                      <p>{detail.resolution_notes}</p>
                    </div>
                  )}

                  <div className="section">
                    <h3>Comments</h3>
                    <div className="comments-list">
                      {detail.comments && detail.comments.length > 0 ? (
                        detail.comments.map((c) => (
                          <div key={c.id} className={`comment ${c.is_internal ? 'internal' : ''}`}>
                            <div className="comment-header">
                              <strong>{c.author_id}</strong>
                              {c.is_internal && <span className="badge internal">Internal</span>}
                              <span className="time">{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                            <p>{c.comment}</p>
                          </div>
                        ))
                      ) : <p className="empty">No comments yet</p>}
                    </div>

                    <div className="add-comment">
                      <textarea rows="3" value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add a comment..." />
                      <label>
                        <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />
                        Internal note (not visible to the submitter)
                      </label>
                      <button className="btn btn-sm btn-primary" disabled={submitting || !commentText.trim()} onClick={addComment}>Post Comment</button>
                    </div>
                  </div>

                  <div className="section actions">
                    <h3>Update</h3>
                    <div className="form-group">
                      <label>Status</label>
                      <select value={updateStatus} onChange={(e) => setUpdateStatus(e.target.value)}>
                        <option value="submitted">Submitted</option>
                        <option value="under_review">Under Review</option>
                        <option value="resolved">Resolved</option>
                        <option value="dismissed">Dismissed</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Assigned To (user ID, optional)</label>
                      <input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Resolution Notes</label>
                      <textarea rows="2" value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} />
                    </div>
                    <button className="btn btn-primary" disabled={submitting} onClick={saveUpdate}>
                      {submitting ? 'Saving...' : 'Save Update'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Acknowledgements                                                   */
/* ---------------------------------------------------------------- */

function AcknowledgementsTab() {
  const [subjectType, setSubjectType] = useState('announcement');
  const [subjectId, setSubjectId] = useState('');
  const [title, setTitle] = useState('');
  const [requiredBy, setRequiredBy] = useState('');
  const [studentQuery, setStudentQuery] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [parents, setParents] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [statusSubjectType, setStatusSubjectType] = useState('announcement');
  const [statusSubjectId, setStatusSubjectId] = useState('');
  const [statusRows, setStatusRows] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);

  useEffect(() => {
    studentsService.listStudents({ limit: 100 }).then((data) => setStudentQuery(data.items || [])).catch(() => {});
  }, []);

  const loadParents = async (studentId) => {
    setSelectedStudentId(studentId);
    setSelectedParentIds([]);
    if (!studentId) { setParents([]); return; }
    try {
      const data = await studentsService.getStudentParents(studentId);
      setParents(data || []);
    } catch (error) {
      toast.error('Failed to load parents for this student: ' + error.message);
    }
  };

  const toggleParent = (userId) => {
    setSelectedParentIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!title.trim() || !subjectId.trim()) {
      toast.error('Title and subject ID are required');
      return;
    }
    if (selectedParentIds.length === 0) {
      toast.error('Select at least one parent to target');
      return;
    }
    setSubmitting(true);
    try {
      const result = await parentServicesService.requestAcknowledgements({
        subject_type: subjectType,
        subject_id: subjectId,
        title,
        target_user_ids: selectedParentIds,
        student_id: selectedStudentId || null,
        required_by: requiredBy || null,
      });
      toast.success(`Requested from ${result.requested_count} parent(s)`);
      setTitle(''); setSubjectId(''); setRequiredBy(''); setSelectedParentIds([]);
    } catch (error) {
      toast.error('Failed to request acknowledgement: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const loadStatus = async () => {
    if (!statusSubjectId.trim()) {
      toast.error('Enter a subject ID to check');
      return;
    }
    setStatusLoading(true);
    try {
      const data = await parentServicesService.getSubjectAcknowledgements(statusSubjectType, statusSubjectId.trim());
      setStatusRows(data || []);
      setStatusLoaded(true);
    } catch (error) {
      toast.error('Failed to load status: ' + error.message);
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <div className="pra-panel pra-ack-grid">
      <div className="pra-card">
        <h3>Request Acknowledgement</h3>
        <form onSubmit={submitRequest}>
          <div className="form-group">
            <label>Subject Type</label>
            <select value={subjectType} onChange={(e) => setSubjectType(e.target.value)}>
              <option value="announcement">Announcement</option>
              <option value="report_card">Report Card</option>
              <option value="policy_document">Policy Document</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Subject ID *</label>
            <input value={subjectId} onChange={(e) => setSubjectId(e.target.value)} placeholder="ID of the announcement/report card/etc." required />
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Label shown to the parent" required />
          </div>
          <div className="form-group">
            <label>Required By (optional)</label>
            <input type="date" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Student</label>
            <select value={selectedStudentId} onChange={(e) => loadParents(e.target.value)}>
              <option value="">— Select a student to find their parents —</option>
              {studentQuery.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.first_name} ${s.last_name}`}</option>)}
            </select>
          </div>
          {parents.length > 0 && (
            <div className="form-group">
              <label>Target Parent(s)</label>
              <div className="pra-checkbox-list">
                {parents.map((p) => (
                  <label key={p.parent_id}>
                    <input
                      type="checkbox"
                      checked={selectedParentIds.includes(p.user_id)}
                      disabled={!p.user_id}
                      onChange={() => toggleParent(p.user_id)}
                    />
                    {p.first_name} {p.last_name} ({p.relationship})
                  </label>
                ))}
              </div>
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Sending...' : 'Request Acknowledgement'}
          </button>
        </form>
      </div>

      <div className="pra-card">
        <h3>Check Status</h3>
        <div className="form-group">
          <label>Subject Type</label>
          <select value={statusSubjectType} onChange={(e) => setStatusSubjectType(e.target.value)}>
            <option value="announcement">Announcement</option>
            <option value="report_card">Report Card</option>
            <option value="policy_document">Policy Document</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label>Subject ID</label>
          <input value={statusSubjectId} onChange={(e) => setStatusSubjectId(e.target.value)} placeholder="Same ID used when requesting" />
        </div>
        <button className="btn btn-secondary" disabled={statusLoading} onClick={loadStatus}>
          {statusLoading ? 'Loading...' : 'Check Status'}
        </button>

        {statusLoaded && (
          statusRows.length === 0 ? (
            <p className="empty pra-mt">No acknowledgement requests found for that subject</p>
          ) : (
            <div className="pra-table pra-mt">
              <table>
                <thead><tr><th>Target</th><th>Acknowledged</th><th>When</th></tr></thead>
                <tbody>
                  {statusRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.target_user_id}</td>
                      <td><StatusBadge status={r.acknowledged ? 'resolved' : 'pending'} /></td>
                      <td>{r.acknowledged_at ? new Date(r.acknowledged_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Fee Reminders                                                      */
/* ---------------------------------------------------------------- */

function FeeRemindersTab() {
  const [settings, setSettings] = useState({ enabled: true, reminder_days_before_due: 7 });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await parentServicesService.getFeeReminderSettings();
      setSettings(data);
    } catch (error) {
      toast.error('Failed to load settings: ' + error.message);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await parentServicesService.getFeeReminderHistory();
      setHistory(data || []);
    } catch (error) {
      toast.error('Failed to load reminder history: ' + error.message);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); loadHistory(); }, [loadSettings, loadHistory]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const data = await parentServicesService.updateFeeReminderSettings(settings);
      setSettings(data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const result = await parentServicesService.runFeeRemindersNow();
      toast.success(`Sent ${result.reminders_sent ?? 0} reminder(s)`);
      loadHistory();
    } catch (error) {
      toast.error('Failed to run reminders: ' + error.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="pra-panel pra-ack-grid">
      <div className="pra-card">
        <h3>Settings</h3>
        {settingsLoading ? (
          <div className="loading"><CircleNotch size={24} weight="bold" /> Loading...</div>
        ) : (
          <>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                />
                {' '}Enabled
              </label>
            </div>
            <div className="form-group">
              <label>Remind this many days before due date</label>
              <input
                type="number"
                min="0"
                value={settings.reminder_days_before_due}
                onChange={(e) => setSettings({ ...settings, reminder_days_before_due: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div className="pra-row-actions">
              <button className="btn btn-primary" disabled={saving} onClick={saveSettings}>{saving ? 'Saving...' : 'Save Settings'}</button>
              <button className="btn btn-secondary" disabled={running} onClick={runNow}>{running ? 'Running...' : 'Run Now'}</button>
            </div>
          </>
        )}
      </div>

      <div className="pra-card pra-card-wide">
        <h3>Reminder History</h3>
        {historyLoading ? (
          <div className="loading"><CircleNotch size={24} weight="bold" /> Loading...</div>
        ) : history.length === 0 ? (
          <p className="empty">No reminders sent yet</p>
        ) : (
          <div className="pra-table">
            <table>
              <thead><tr><th>Student</th><th>Channel</th><th>Days Until Due</th><th>Recipient</th><th>Status</th><th>Sent</th></tr></thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id}>
                    <td>{r.student_id}</td>
                    <td>{r.channel}</td>
                    <td>{r.days_until_due}</td>
                    <td>{r.recipient}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParentRequestsAdminPage;
